/**
 * ProtectedRoute Component
 *
 * Protects routes based on authentication status and user role.
 * Prevents cross-access between Admin and User apps.
 */

import { Navigate, Outlet, useLocation } from "react-router-dom";
import { getAuthState, UserRole } from "@/utils/auth/authUtils";
import { useEffect, useState } from "react";

interface ProtectedRouteProps {
  /**
   * Required role to access the route
   * - "admin": Only admin users can access
   * - "user": Only regular users can access
   * - null: Any authenticated user can access
   */
  requiredRole?: UserRole;

  /**
   * Redirect path when unauthorized
   * Default: "/"
   */
  redirectTo?: string;

  /**
   * Whether to allow unauthenticated access
   * Default: false
   */
  allowUnauthenticated?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredRole,
  redirectTo = "/",
  allowUnauthenticated = false,
}) => {
  const location = useLocation();
  const [authState, setAuthState] = useState(getAuthState());

  // Update auth state when location changes (in case auth changed)
  useEffect(() => {
    setAuthState(getAuthState());
  }, [location.pathname]);

  // If unauthenticated access is allowed, render children
  if (allowUnauthenticated) {
    return <Outlet />;
  }

  // If not authenticated, redirect to home
  if (!authState.isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // If no specific role required, allow any authenticated user
  if (!requiredRole) {
    return <Outlet />;
  }

  // Check role-specific access
  if (requiredRole === "admin" && !authState.isAdmin) {
    // User is logged in but not admin - redirect to home
    return <Navigate to={redirectTo} replace />;
  }

  if (requiredRole === "user" && !authState.isUser) {
    // Admin is logged in but trying to access user routes - redirect to home
    return <Navigate to={redirectTo} replace />;
  }

  // User has the required role
  return <Outlet />;
};

export default ProtectedRoute;
